import { useCallback, useRef, useState } from 'react';
import { useInviteWitness } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useInviteWitness';
import type {
	BeginSigningInput,
	WitnessBeforeSigningParams,
} from './useWitnessBeforeSigning.types';

/**
 * Gates a manager's signature on nominating a witness first, mirroring the investor flow: the
 * witness step runs before eID Easy signing and the witness is invited once the signature completes.
 */
export const useWitnessBeforeSigning = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	startSign,
}: WitnessBeforeSigningParams) => {
	const { mutate: inviteWitness, isPending: isInviting } = useInviteWitness();
	const [signatureId, setSignatureId] = useState<string | null>(null);
	const [requiresWitness, setRequiresWitness] = useState(false);
	const [isWitnessStepActive, setIsWitnessStepActive] = useState(false);
	const [witnessEmail, setWitnessEmail] = useState('');
	const [inviteFailed, setInviteFailed] = useState(false);
	// Read from the eID Easy completion callback, which is wired before this state settles.
	const pendingInviteRef = useRef<{ parentSignatureId: string; witnessEmail: string } | null>(
		null
	);

	const sendInvite = useCallback(
		(parentSignatureId: string, email: string) => {
			setInviteFailed(false);
			inviteWitness(
				{
					dealId,
					programmeId,
					documentWorkflowInstanceId,
					parentSignatureId,
					witnessEmail: email,
				},
				{ onError: () => setInviteFailed(true) }
			);
		},
		[dealId, programmeId, documentWorkflowInstanceId, inviteWitness]
	);

	const begin = useCallback(
		(input: BeginSigningInput) => {
			setSignatureId(input.signatureId);
			setRequiresWitness(input.requiresWitness);
			setInviteFailed(false);
			pendingInviteRef.current = null;
			if (input.requiresWitness) {
				setWitnessEmail(input.witnessEmail ?? '');
				setIsWitnessStepActive(true);
				return;
			}
			startSign(input.signatureId);
		},
		[startSign]
	);

	const continueToSign = useCallback(
		(email: string) => {
			if (!signatureId) {
				return;
			}
			setWitnessEmail(email);
			pendingInviteRef.current = { parentSignatureId: signatureId, witnessEmail: email };
			setIsWitnessStepActive(false);
			startSign(signatureId);
		},
		[signatureId, startSign]
	);

	const backToWitness = useCallback(() => {
		pendingInviteRef.current = null;
		setIsWitnessStepActive(true);
	}, []);

	const onSigningComplete = useCallback(() => {
		const pendingInvite = pendingInviteRef.current;
		if (!pendingInvite) {
			return;
		}
		pendingInviteRef.current = null;
		sendInvite(pendingInvite.parentSignatureId, pendingInvite.witnessEmail);
	}, [sendInvite]);

	const retryInvite = useCallback(() => {
		if (signatureId && witnessEmail) {
			sendInvite(signatureId, witnessEmail);
		}
	}, [signatureId, witnessEmail, sendInvite]);

	const reset = useCallback(() => {
		setSignatureId(null);
		setRequiresWitness(false);
		setIsWitnessStepActive(false);
		setWitnessEmail('');
		setInviteFailed(false);
		pendingInviteRef.current = null;
	}, []);

	return {
		begin,
		continueToSign,
		backToWitness,
		onSigningComplete,
		retryInvite,
		reset,
		requiresWitness,
		isWitnessStepActive,
		witnessEmail,
		inviteFailed,
		isInviting,
	};
};
